import { Link } from "react-router-dom";
import { Instagram, Linkedin, Twitter, Youtube, Heart } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Work", to: "/work" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
  { label: "Areas We Serve", to: "/locations" },
  { label: "Contact", to: "/contact" },
];

import { SERVICES as SERVICE_LINKS } from "@/data/services";

const footerTitle = (t) =>
  t
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bSeo\b/g, "SEO")
    .replace(/\bAi\b/g, "AI")
    .replace(/\bUgc\b/g, "UGC");

const SOCIALS = [
  { icon: Instagram, label: "Instagram" },
  { icon: Linkedin, label: "LinkedIn" },
  { icon: Twitter, label: "Twitter / X" },
  { icon: Youtube, label: "YouTube" },
];

export const Footer = () => (
  <footer data-testid="footer" className="relative bg-black border-t border-white/[0.06] overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-10">
      <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-12 md:gap-8 mb-20">
        <div>
          <img
            src="/images/brandzenith-logo.png"
            alt="BrandZenith — AI-Powered Brand Growth Studio"
            className="w-56 -ml-2"
          />
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#8B2CFF]/80">
            Strategy × Creativity × AI × Growth
          </p>
          <div className="mt-8 flex items-center gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                data-testid={`footer-social-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                href="#"
                onClick={(e) => e.preventDefault()}
                aria-label={`${s.label} (link coming soon)`}
                className="w-10 h-10 rounded-md border border-white/10 flex items-center justify-center text-[#A6A6B0] hover:text-[#C43CFF] hover:border-[#8B2CFF]/50 hover:shadow-[0_0_18px_-4px_rgba(139,44,255,0.6)] transition-[color,border-color,box-shadow] duration-300"
              >
                <s.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-white/50 mb-6">Navigate</h3>
          <ul className="space-y-3.5">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-[#A6A6B0] hover:text-white transition-colors duration-300"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-white/50 mb-6">Services</h3>
          <ul className="space-y-3.5">
            {SERVICE_LINKS.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-sm text-[#A6A6B0] hover:text-white transition-colors duration-300"
                >
                  {footerTitle(s.title)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.22em] text-white/50 mb-6">Studio</h3>
          <p className="text-sm text-[#A6A6B0] leading-relaxed">
            BrandZenith is an AI-powered brand growth studio. Strategy, creativity and technology
            under one roof.
          </p>
        </div>
      </div>

      <div
        aria-hidden="true"
        className="select-none text-center font-display font-extrabold uppercase tracking-tighter leading-none text-[13vw] text-white/[0.035] pointer-events-none"
      >
        BrandZenith
      </div>

      <div className="mt-10 pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-[#A6A6B0]/70 flex items-center gap-1.5">
          © 2026 BrandZenith. All rights reserved. · Made with
          <Heart className="w-3 h-3 text-[#C43CFF] fill-[#C43CFF]" aria-label="love" />
          by Ecorp Technology
        </p>
      </div>
    </div>
  </footer>
);
